import React from 'react';
import styled from 'styled-components';
import { FaChartLine, FaChartPie, FaRegFileAlt } from 'react-icons/fa';
import { designTokens } from '../foundation/DesignTokens';
import { Drawer, DrawerList, DrawerItem } from './Drawer';

// Styled sidebar title
const SidebarTitle = styled.div`
  font-family: ${designTokens.fonts.heading};
  font-weight: 800;
  font-size: 1.25rem;
  color: ${designTokens.colors.gray[900]};
  margin-bottom: ${designTokens.spacing.xl};
  padding-left: 12px;
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};

  @media (max-width: 900px) {
    font-size: 1.1rem;
    margin-bottom: ${designTokens.spacing.lg};
  }
`;

// Enhanced drawer item with better styling
const NavItem = styled(DrawerItem)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${designTokens.radius.md};
  color: ${designTokens.colors.gray[700]};
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  margin-bottom: 4px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  width: 100%;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: ${designTokens.colors.gray[50]};
    color: ${designTokens.colors.gray[900]};
  }

  ${(props) =>
    props.active &&
    `
    background-color: rgba(79, 70, 229, 0.08);
    color: #4F46E5;
    font-weight: 600;
    border-color: rgba(79, 70, 229, 0.15);
  `}

  ${(props) =>
    props.disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
  `}

  @media (max-width: 900px) {
    font-size: 0.87rem;
    padding: 10px 14px;
    gap: 10px;
  }
`;

// Icon wrapper
const NavIcon = styled.span`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.active ? 1 : 0.7)};
  transition: opacity 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 900px) {
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

// Navigation items configuration for Analytics/Statistics
const navigationItems = [
  {
    id: 'analiticasAvanzadas',
    label: 'Estadísticas',
    Icon: FaChartPie,
    scrollTarget: null,
  },
];

const AnalyticsNavigation = ({
  activeView = 'estadisticas',
  onViewChange,
  showTemplateAnalysis = false,
}) => {
  const handleNavClick = (item) => {
    if (item.disabled) return;

    // Update active view
    if (onViewChange) {
      onViewChange(item.id);
    }

    // Find the scrollable content wrapper
    const contentWrapper = document.querySelector('[data-analytics-content]');

    // Scroll to section
    if (item.scrollTarget) {
      const element = document.getElementById(item.scrollTarget);
      if (element) {
        // Use scrollIntoView with nearest container option
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Scroll to top - try content wrapper first, then window
      if (contentWrapper) {
        contentWrapper.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const items = [
    ...navigationItems,
    ...(showTemplateAnalysis
      ? [
          {
            id: 'plantillas',
            label: 'Análisis plantillas',
            Icon: FaRegFileAlt,
            scrollTarget: null,
          },
        ]
      : []),
  ];

  return (
    <Drawer>
      <SidebarTitle>Reportes</SidebarTitle>

      <DrawerList>
        {items.map((item) => (
          <NavItem
            key={item.id}
            active={activeView === item.id}
            disabled={item.disabled}
            onClick={() => handleNavClick(item)}>
            <NavIcon active={activeView === item.id}><item.Icon /></NavIcon>
            {item.label}
          </NavItem>
        ))}
      </DrawerList>
    </Drawer>
  );
};

export default AnalyticsNavigation;
